import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@lwt-helix/buttons';
import Modal from '@lwt-helix/modal';
import Popover from '@lwt-helix/popover';
import Loader from '@lwt-helix/loader';
import { CustomInput, Input, Label } from '@lwt-helix/controls';
import { useCommonStore, useSearchStore, useSavedAgentsStore } from '../../../store/store';
import {
    favoriteAgentTerms as favTerms,
    popoverTerms
} from '../../../agent-production/constants/agentProductionConstants';
import { buttonTerms, modules, searchTerms } from '../../../constants';
import { setStateData  } from '../../../common/helpers/state';
import ToastContainerPortal from '../../../common/components/search/menus/ToastContainerPortal';
import { showToast } from '../../../common/helpers/toast';
import { checkReservedName } from '../helpers/checkReservedName';

const AddAgentListPopover = () => {

    const listNameInput = useRef();

    const {
        agentLists,
        agentListToUpdate,
        agentListsCheckedValues,
        createAgentsList,
        handleUpdateAgentNameSelectedList,
        popoverDataForAddAgentList,
        toggleAgentListsPopover
    } = useCommonStore(state => ({
        agentLists: state.agentLists,
        agentListToUpdate: state.agentListToUpdate,
        agentListsCheckedValues: state.agentListsCheckedValues,
        createAgentsList: state.createAgentsList,
        handleUpdateAgentNameSelectedList: state.handleUpdateAgentNameSelectedList,
        popoverDataForAddAgentList: state.popoverDataForAddAgentList,
        toggleAgentListsPopover: state.toggleAgentListsPopover
    }));

    const currentModule = useSearchStore(state => state.selectedModule);

    const {
        selectedList,
        isLoading,
    } = useSavedAgentsStore(state => ({
        selectedList: state.selectedListByModule[currentModule],
        isLoading: state.isLoading,
    }));


    let mlsId = useSearchStore(state => state[state.selectedModule]?.search)?.mlsId;
    const agentNameSearch = useSearchStore(state => state[modules.proficiencyMetrics.agents]);
    const setListInSearchByAgentSearch = useSearchStore(state => state.setListInSearchByAgentSearch);

    const { agentId, rowIndex, tableId, popoverIsOpen, suggAgentsTabId } = popoverDataForAddAgentList;

    const [state, setState] = useState({
        agentListName: undefined,
        savedAgentLists: agentLists,
        shouldShowModal: false,
        popoverIsOpen: false,
        toastProps: undefined,
        currentRowIndex: undefined,
        reservedNameWarning: '',
    });

    useEffect(() => {
        if (
                (currentModule === modules.proficiencyMetrics.suggestedAgents
                || currentModule === modules.proficiencyMetrics.savedAgents)
                && selectedList
                && agentLists?.length
            ) {
            const mlsId = selectedList.mlsId;
            setStateData('savedAgentLists', agentLists.filter(list => list?.mlsId === mlsId), setState);
        };
    }, [currentModule, selectedList, agentLists]);

    const setToastProps = (type, message) => showToast(type, message, setState);

    const toggleModal = () => setStateData('shouldShowModal', !state.shouldShowModal, setState);

    const handleAgentListNameChange = (e) => {
        const value = e.target.value.trim();
        setStateData('agentListName', value, setState);

        if (value.toLowerCase() === favTerms.reservedListName.toLowerCase()) {
            setStateData('reservedNameWarning', favTerms.reservedListWarning, setState);
        } else {
            setStateData('reservedNameWarning', '', setState);
        }
    };

    const handleChecked = (event, listId, listName) => {
        event.preventDefault();
        (async () => {
            const list = agentLists.find(list => list?.agentListId === listId);
            const wasAgentRemoved = list?.agentIds.includes(agentId);
            const minAgentsAllowed = 1;
            if (!event.target.checked && list.agentIds.length === minAgentsAllowed) {
                toggleAgentListsPopover();
                setToastProps('error', popoverTerms.atLeastOneAgentInList);
                return;
            }
            await handleUpdateAgentNameSelectedList(listId, agentId, listName, agentNameSearch, setListInSearchByAgentSearch);
            toggleAgentListsPopover();
            setToastProps('success', wasAgentRemoved
                ? popoverTerms.agentRemovedFromList
                : popoverTerms.agentAddedToList
            );
        })()
    };

    const showAgentListModal = () => {
        setStateData('reservedNameWarning', '', setState);
        toggleModal();
    };

    const cancelAddAgentList = () => {
        toggleModal();
        setStateData('agentListName', '', setState);
        setStateData('reservedNameWarning', '', setState);
    };

    const createAddAgentList = async () => {
        if (isLoading) return;
        const agentListName = listNameInput.current.value.trim();
        if (agentListName.toLowerCase() === favTerms.reservedListName.toLowerCase()) {
            setStateData('reservedNameWarning', favTerms.reservedListWarning, setState);
            return;
        }
        const agentListNameExists = agentLists.filter(list => list?.mlsId === mlsId).find(list =>
            list?.name.toLowerCase() === agentListName.toLowerCase()
        );
        if (!agentListNameExists) {
            setStateData('popoverIsOpen', false, setState);
            toggleAgentListsPopover();
            await createAgentsList(mlsId, agentListName, agentId);
            setStateData('agentListName', undefined, setState);
            toggleModal();
        }
        setToastProps(agentListNameExists
            ? 'error'
            : 'success',
            agentListNameExists
                ? favTerms.chooseDifferentName
                : favTerms.agentListCreated(agentListName)
        );
    };

    const modalButtons = (
        <>
            <Button
                dataLwtId='cancel-add-agent-list' color='secondary'
                onClick={cancelAddAgentList}
                disabled={isLoading}
            >
                {buttonTerms.cancel}
            </Button>
            <Button
                dataLwtId='save-add-agent-list'
                color='primary'
                onClick={createAddAgentList}
                disabled={!state.agentListName || isLoading || !!state.reservedNameWarning}
            >
                {buttonTerms.save}
            </Button>
        </>
    );

    const modalChildren = (
        <div className='modal-warning'>
            <p className='m-0 text-danger warning-container'>
                {state.reservedNameWarning}
            </p>
            <Label dataLwtId="list-name-label" className='font-weight-bold'>
                {searchTerms.nameYourList}
            </Label>
            <Input
                className='mt-2'
                dataLwtId="list-name-input"
                defaultValue={state.agentListName}
                innerRef={listNameInput}
                maxLength={55}
                onChange={handleAgentListNameChange}
                placeholder={favTerms.enterListName}
            />
            <p className='text-muted'>{favTerms.chooseAName55Char}.</p>
        </div>
    );

    useEffect(() => {
        if (!agentLists) return;
        setStateData('savedAgentLists', agentLists.filter(list => list?.mlsId === mlsId), setState);
    }, [agentLists, mlsId]);

    const renderedAgentLists = state.savedAgentLists?.map(list => (
        <React.Fragment key={list?.agentListId}>
            {list?.name === agentListToUpdate
                ?
                <div className='d-flex align-items-start checkbox-loader'>
                    <Loader />
                    <span>{list?.name}</span>
                </div>
                :
                <CustomInput
                    checked={agentListsCheckedValues?.[list?.agentListId] || false}
                    id={list?.agentListId}
                    label={list?.name}
                    onChange={(event) => handleChecked(
                        event,
                        list?.agentListId,
                        list?.name
                    )}
                    type={popoverTerms.checkbox}
                />
            }
        </React.Fragment>
    ));

    const popoverBody = (
        <>
            <div className='fav-agents-lists-popover'>
                {mlsId && renderedAgentLists}
            </div>
            <hr />
            <div className='d-flex justify-content-center w-100'>
                <button className='btn btn-light text-capitalize' onClick={showAgentListModal}>
                    {popoverTerms.addList}
                </button>
            </div>
        </>
    );

    const targetId = popoverTerms.starIcon(tableId, rowIndex, suggAgentsTabId);
    const targetElement = document.getElementById(targetId);

    return (
        <>
            {popoverIsOpen && !!targetElement && <Popover
                id={popoverTerms.favoriteAgentPopover}
                target={targetElement}
                placement={popoverTerms.bottom}
                trigger={!state.shouldShowModal ? popoverTerms.legacy : undefined}
                header={favTerms.savedAgentLists}
                toggle={toggleAgentListsPopover}
                isOpen={popoverIsOpen}
                size={popoverTerms.auto}
                body={popoverBody}
                modifiers={{
                    preventOverflow: { boundariesElement: 'viewport' },
                    computeStyle: {
                        adaptive: false,
                        gpuAcceleration: false,
                        maxHeight: 800
                    }
                }}
            />}
            <ToastContainerPortal toastProps={state.toastProps} />
            <Modal
                children={modalChildren}
                buttons={modalButtons}
                onClose={toggleModal}
                onOpened={() => checkReservedName(listNameInput, setState, setStateData)}
                show={state.shouldShowModal}
                title={favTerms.addAgentList}
            />
        </>
    );
};

export default AddAgentListPopover;
